import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { IconWalls } from "$root/components/IconWall";
import { PageHeader } from "$root/components/PageHeader";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { useQueries } from "@tanstack/react-query";
import { Set } from "immutable";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import MarketViewsList from "./MarketViewList";
import { portfolioStudioDefaultTabOrdering, PortfolioStudioTab } from "./portfolio-studio-tabs";
import PortfolioList from "./PortfolioList";
import ReferenceList from "./ReferenceList";
import UniverseList from "./UniverseList";
import { PortfolioStudioTitleActions } from "./PortfolioStudioTitleActions";

type PortfoliosStudioProps = {
	tab?: string;
};

const PortfoliosStudio = ({ tab = PortfolioStudioTab.Portfolios }: PortfoliosStudioProps): React.JSX.Element => {
	const location = useLocation();
	const { replace } = useTypedNavigation();
	const crumbs = useCrumbs();
	const currentTabName = useMemo(() => {
		const queryParamTab = new URLSearchParams(location.search).get("tab");
		const candidate = queryParamTab && portfolioStudioDefaultTabOrdering.find((v) => v === queryParamTab);
		if (!queryParamTab || !candidate) {
			replace("PortfoliosStudio", { tab: PortfolioStudioTab.Portfolios });
			return PortfolioStudioTab.Portfolios;
		}
		return candidate;
	}, [location.search, replace]);

	const [visitedTabs, setVisitedTabs] = useState<Set<typeof currentTabName>>(() => Set());

	useEffect(() => {
		setVisitedTabs((v) => v.add(currentTabName));
	}, [currentTabName]);

	//splitted platform requests
	const portfoliosStudio = useQueries({
		queries: [
			{
				...referenceRepository.referenceList.getQueryConfig(),
				enabled: visitedTabs.includes("References"),
			},
			{
				...universeRepository.listBaseAndGroup.getQueryConfig(),
				enabled: visitedTabs.includes("Universes"),
			},
			{
				...marketViewRepository.list.getQueryConfig(),
				enabled: visitedTabs.includes("MarketViews"),
			},
			{
				...referenceRepository.benchmarkList.getQueryConfig(),
				enabled: visitedTabs.includes("References"),
			},
		],
	});

	const [reference, universe, marketViews, benchmarks] = portfoliosStudio;

	const dataStudio = useMemo(
		() =>
			({
				References: {
					reference,
					benchmarks,
				},
				Universes: { universe },
				MarketViews: { list: marketViews, settings: { isLoading: false, isError: false } },
			}) satisfies Record<Exclude<PortfolioStudioTab, "Portfolios">, unknown>,
		[benchmarks, marketViews, reference, universe],
	);
	const iconWall = useMemo((): ReactNode => {
		if (currentTabName === PortfolioStudioTab.Portfolios) {
			return null;
		}

		if (currentTabName === PortfolioStudioTab.References) {
			if (dataStudio[currentTabName].benchmarks.isLoading || dataStudio[currentTabName].reference.isLoading) {
				return <IconWalls.LoadingData />;
			}

			if (dataStudio[currentTabName].benchmarks.isError || dataStudio[currentTabName].reference.isError) {
				return <IconWalls.ErrorData />;
			}

			if (
				(dataStudio[currentTabName].benchmarks.data?.length ?? 0) +
					(dataStudio[currentTabName].reference.data?.length ?? 0) ===
				0
			) {
				return <IconWalls.ReferenceListEmptyData />;
			}

			return null;
		}

		if (currentTabName === PortfolioStudioTab.Universes) {
			if (dataStudio[currentTabName].universe.isLoading) {
				return <IconWalls.LoadingData />;
			}

			if (dataStudio[currentTabName].universe.isError) {
				return <IconWalls.ErrorData />;
			}

			if ((dataStudio[currentTabName].universe.data?.length ?? 0) === 0) {
				return <IconWalls.UniverseListEmptyData />;
			}

			return null;
		}

		if (dataStudio[currentTabName].list.isLoading || dataStudio[currentTabName].settings.isLoading) {
			return <IconWalls.LoadingData />;
		}

		if (
			dataStudio[currentTabName].list.isError ||
			dataStudio[currentTabName].settings.isError ||
			portfoliosStudio === undefined
		) {
			return <IconWalls.ErrorData />;
		}

		if (currentTabName === PortfolioStudioTab.MarketViews) {
			if (dataStudio[currentTabName].list?.data?.length === 0) {
				return <IconWalls.MarketViewListEmptyData />;
			}
		}
		return null;
	}, [currentTabName, dataStudio, portfoliosStudio]);

	const tabs = useMemo(() => {
		return {
			[PortfolioStudioTab.Portfolios]: <PortfolioList />,
			[PortfolioStudioTab.References]: iconWall || (
				<ReferenceList references={reference?.data ?? []} benchmarks={benchmarks?.data ?? []} />
			),
			[PortfolioStudioTab.Universes]: iconWall || <UniverseList universes={universe?.data ?? []} />,
			[PortfolioStudioTab.MarketViews]: iconWall || <MarketViewsList markets={marketViews?.data ?? []} />,
		};
	}, [iconWall, reference?.data, benchmarks?.data, universe?.data, marketViews?.data]);

	return (
		<>
			<PageHeader
				titleAction={<PortfolioStudioTitleActions />}
				crumbs={crumbs.portfolioStudio(tab as PortfolioStudioTab)}
			/>

			<div className="flex-1 min-h-0 flex flex-col relative z-0">{tabs[currentTabName]}</div>
		</>
	);
};

export default PortfoliosStudio;
